import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { CircleMarker, MapContainer, Polyline, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, SegmentedControl, Skeleton } from '@liftango/ui-elements';
import { opsColors, opsElevation, opsFontFamily, opsFontSize, opsFontWeight, opsRadius, opsSpace } from '../../../../../theme/opsTokens';
import { activeAt } from '../../../../../services/simulation/runReport';
import { useGoogleBasemap } from '../../../../../services/googleTiles/useGoogleBasemap';
import { buildVehicleTracks, vehiclePositionAt } from '../../../../../utils/simulation/vehiclePlayback';
import GoogleTileLayer from '../../../GoogleTileLayer';
import VehiclePlaybackLayer from '../VehiclePlaybackLayer';
import type { RunReport } from '../useRunReport';
import { activityTimes, allTrips, playbackSegments, RECENT_TRIP_WINDOW_MS, requestsBy, tripsAt, unmatchedRequests, type MapRequest } from './mapData';
import { SourceProblem } from './SourceProblem';
import { formatClock } from './reportFormat';
import type { Playback } from './usePlayback';

export type MapMode = 'time' | 'all';

const MAP_HEIGHT = '520px';
const FIT_PADDING: L.PointTuple = [24, 24];
const DEFAULT_CENTER: L.LatLngTuple = [0, 0];
const DEFAULT_ZOOM = 12;
const FOLLOW_MARGIN = -0.2;
const REQUEST_RADIUS = 5;
const SWATCH = { width: '14px', height: '3px' } as const;
const DOT = { width: '8px', height: '8px' } as const;
// Shared style objects: react-leaflet restyles a layer whenever its pathOptions object changes.
const LIVE_TRIP: L.PathOptions = { color: opsColors.semantic.info.color, weight: 3 };
const RECENT_TRIP: L.PathOptions = { color: opsColors.neutral.inkMute, weight: 2, dashArray: '6 6' };
const DAY_TRIP: L.PathOptions = { color: opsColors.semantic.info.color, weight: 2, opacity: 0.35 };
const UNMATCHED: L.PathOptions = { color: opsColors.neutral.panel, weight: 1, fillColor: opsColors.semantic.error.color, fillOpacity: 1 };
const NO_REQUESTS: readonly MapRequest[] = [];

const isMapMode = (value: string): value is MapMode => value === 'time' || value === 'all';

/** Fits the run once: its trips, or its unmatched requests when no trip has a location. */
function FitToRun({ points, fitKey }: { points: readonly L.LatLngTuple[]; fitKey: string }) {
  const map = useMap();
  const fitted = useRef<string | null>(null);
  useEffect(() => {
    if (fitted.current === fitKey || points.length === 0) return;
    fitted.current = fitKey;
    const bounds = L.latLngBounds([...points]);
    if (bounds.isValid()) map.fitBounds(bounds, { padding: FIT_PADDING, animate: false });
  }, [map, points, fitKey]);
  return null;
}

/** Keeps the selected vehicle in view while the clock moves. */
function FollowVehicle({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    if (!map.getBounds().pad(FOLLOW_MARGIN).contains([lat, lng])) map.panTo([lat, lng], { animate: false });
  }, [map, lat, lng]);
  return null;
}

export interface ReportMapProps {
  readonly report: RunReport;
  readonly playback: Playback;
  readonly mode: MapMode;
  readonly onModeChange: (mode: MapMode) => void;
  readonly selectedShiftId: string | null;
  readonly hiddenShiftIds: ReadonlySet<string>;
  readonly fitKey: string;
  readonly children?: React.ReactNode;
}

export function ReportMap({ report, playback, mode, onModeChange, selectedShiftId, hiddenShiftIds, fitKey, children }: ReportMapProps) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const googleBasemap = useGoogleBasemap();
  const derived = report.derived;
  const routes = report.paths.status === 'ready' ? report.paths.data : null;
  const unmatched = report.unmatched.status === 'ready' ? report.unmatched.data : null;
  const timeMs = mode === 'time' ? playback.timeMs : null;

  const everyTrip = useMemo(() => (derived ? allTrips(derived.rides) : []), [derived]);
  const everyRequest = useMemo(() => (unmatched ? unmatchedRequests(unmatched) : NO_REQUESTS), [unmatched]);
  const fitPoints = useMemo(
    () => (everyTrip.length > 0 ? everyTrip.flatMap((trip) => trip.line) : everyRequest.map((request) => request.at)),
    [everyTrip, everyRequest]
  );
  const shownTrips = useMemo(() => everyTrip.filter((trip) => !hiddenShiftIds.has(trip.shiftId)), [everyTrip, hiddenShiftIds]);
  const segments = useMemo(
    () => (routes && derived ? playbackSegments(routes, derived.rides).filter((segment) => !(segment.vehicleShiftId && hiddenShiftIds.has(segment.vehicleShiftId))) : null),
    [routes, derived, hiddenShiftIds]
  );
  const times = useMemo(() => (report.activities.status === 'ready' ? activityTimes(report.activities.data) : null), [report.activities]);
  const tracks = useMemo(() => (segments && times ? buildVehicleTracks(segments, times) : []), [segments, times]);
  const now = timeMs != null ? tripsAt(shownTrips, timeMs) : null;
  const requests = mode === 'time' ? requestsBy(everyRequest, timeMs) : everyRequest;
  const active = derived && timeMs != null ? activeAt(timeMs, derived.rides, derived.shifts) : null;
  const followed = selectedShiftId && timeMs != null ? tracks.find((track) => track.vehicleShiftId === selectedShiftId) : undefined;
  const followedAt = followed && timeMs != null ? vehiclePositionAt(followed, timeMs) : null;
  const pathsNote =
    report.paths.status === 'error' ? 'pathsUnavailable' : report.paths.status === 'ready' || !derived ? null : 'pathsLoading';

  return (
    <Card padding="none" data-testid="sim-results.report.map" style={{ overflow: 'hidden' }}>
      <Box sx={{ position: 'relative', height: MAP_HEIGHT, isolation: 'isolate' }}>
        {derived ? (
          <MapContainer
            preferCanvas
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            zoomControl={false}
            style={{ width: '100%', height: '100%', zIndex: 0 }}
          >
            {googleBasemap.apiKey ? (
              <GoogleTileLayer apiKey={googleBasemap.apiKey} mapType="roadmap" />
            ) : (
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
            )}
            <ZoomControl position="bottomright" />
            <FitToRun points={fitPoints} fitKey={fitKey} />
            {followedAt ? <FollowVehicle lat={followedAt.position[0]} lng={followedAt.position[1]} /> : null}
            {mode === 'all' ? shownTrips.map((trip) => <Polyline key={trip.rideId} positions={trip.line} interactive={false} pathOptions={DAY_TRIP} />) : null}
            {now?.recent.map((trip) => <Polyline key={`recent-${trip.rideId}`} positions={trip.line} interactive={false} pathOptions={RECENT_TRIP} />)}
            {now?.inProgress.map((trip) => <Polyline key={`live-${trip.rideId}`} positions={trip.line} interactive={false} pathOptions={LIVE_TRIP} />)}
            {requests.map((request) => (
              <CircleMarker key={request.key} center={request.at} radius={REQUEST_RADIUS} interactive={false} pathOptions={UNMATCHED} />
            ))}
            {segments && times && timeMs != null ? <VehiclePlaybackLayer segments={segments} activityTimes={times} timeMs={timeMs} /> : null}
          </MapContainer>
        ) : report.activities.status === 'error' || report.vehicles.status === 'error' ? (
          <Box sx={{ height: '100%', display: 'grid', alignContent: 'center' }}>
            <SourceProblem report={report} sources={['activities', 'vehicles']} />
          </Box>
        ) : (
          <Skeleton width="100%" height={MAP_HEIGHT} aria-label={t('results.report.loading')} />
        )}
        <Overlay corner="top-left">
          <SegmentedControl
            aria-label={t('results.report.map.modeLabel')}
            value={mode}
            onChange={(value) => isMapMode(value) && onModeChange(value)}
            options={[
              { value: 'time', label: t('results.report.map.atTime') },
              { value: 'all', label: t('results.report.map.allDay') },
            ]}
          />
          {pathsNote ? (
            <Box data-testid="sim-results.report.map.paths-note" sx={{ mt: opsSpace[1], fontSize: opsFontSize.meta, color: opsColors.neutral.inkMute }}>
              {t(`results.report.map.${pathsNote}`)}
            </Box>
          ) : null}
        </Overlay>
        {timeMs != null && active ? (
          <Overlay corner="top-right">
            {/* Changes every frame while playing; screen readers get the time from the scrubber instead. */}
            <Box data-testid="sim-results.report.map.time-card" role="status" aria-live="off" sx={{ display: 'grid', gap: opsSpace[0.5] }}>
              <Box component="span" sx={{ fontSize: opsFontSize.eyebrow, fontWeight: opsFontWeight.bold, textTransform: 'uppercase', color: opsColors.neutral.inkMute }}>
                {t('results.report.map.time')}
              </Box>
              <Box component="span" sx={{ fontFamily: opsFontFamily.mono, fontSize: opsFontSize.h1, fontWeight: opsFontWeight.bold, color: opsColors.neutral.ink }}>
                {formatClock(timeMs, locale)}
              </Box>
              <Box component="span" sx={{ fontSize: opsFontSize.xs, color: opsColors.neutral.inkSoft }}>
                {t('results.report.map.activity', {
                  trips: t('results.report.map.trips', { count: active.tripsInProgress }),
                  vehicles: t('results.report.map.vehiclesOut', { count: active.vehiclesOut }),
                })}
              </Box>
            </Box>
          </Overlay>
        ) : null}
        <Overlay corner="bottom-left">
          <MapLegend />
        </Overlay>
      </Box>
      {children}
    </Card>
  );
}

const CORNERS = {
  'top-left': { top: opsSpace[3], left: opsSpace[3] },
  'top-right': { top: opsSpace[3], right: opsSpace[3] },
  'bottom-left': { bottom: opsSpace[3], left: opsSpace[3] },
} as const;

function Overlay({ corner, children }: { corner: keyof typeof CORNERS; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 1,
        ...CORNERS[corner],
        backgroundColor: opsColors.neutral.panel,
        borderRadius: opsRadius.md,
        boxShadow: opsElevation.raised.shadow,
        p: opsSpace[2],
      }}
    >
      {children}
    </Box>
  );
}

function MapLegend() {
  const { t } = useTranslation('simulation');
  const item = (key: string, swatch: React.ReactNode, options?: Record<string, unknown>) => (
    <Box key={key} component="span" sx={{ display: 'flex', alignItems: 'center', gap: opsSpace[1] }}>
      <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {swatch}
      </Box>
      {t(`results.report.map.legend.${key}`, options)}
    </Box>
  );
  return (
    <Box data-testid="sim-results.report.map.legend" role="group" aria-label={t('results.report.map.legendLabel')} sx={{ display: 'grid', gap: opsSpace[1], fontSize: opsFontSize.meta, color: opsColors.neutral.inkSoft }}>
      {item('inProgress', <Box component="span" sx={{ ...SWATCH, backgroundColor: opsColors.semantic.info.color }} />)}
      {item('recent', <Box component="span" sx={{ ...SWATCH, borderTop: `${SWATCH.height} dashed ${opsColors.neutral.inkMute}` }} />, { minutes: RECENT_TRIP_WINDOW_MS / 60_000 })}
      {item('vehicle', <Box component="span" sx={{ ...DOT, borderRadius: opsRadius.pill, backgroundColor: opsColors.neutral.ink }} />)}
      {item('unmatched', <Box component="span" sx={{ ...DOT, borderRadius: opsRadius.pill, backgroundColor: opsColors.semantic.error.color }} />)}
    </Box>
  );
}
